'use client'

import {type OmitStrict} from 'type-zoo'
import {Carousel, CarouselContent, CarouselItem} from '~/design-system/foundations'
import {useCarousel} from '~/design-system/foundations/Carousel'
import {
  HgButtonLink,
  HgUiControls,
  type HgArticleListingStackedComponentProps,
  type HgButtonLinkProps,
} from '~/design-system/hg/components'
import HgArticleListingStacked from '../../components/HgArticleListing/_components/Stacked'

export type HgArticleListingOverflowProps = {
  articles: Array<
    OmitStrict<
      HgArticleListingStackedComponentProps,
      'variant' | 'aspectRatio' | 'textAlignment' | 'size'
    >
  >
  showSubheading?: boolean
  buttonLinkProps?: OmitStrict<HgButtonLinkProps, 'variant'>
}

const ArticleListingsContainer = ({
  articles,
  showSubheading = true,
  buttonLinkProps,
}: HgArticleListingOverflowProps) => {
  const {scrollNext, scrollPrev, currentIndex} = useCarousel()
  const shouldShowSubheading =
    showSubheading && articles.every(article => !!article.subheading)

  return (
    <>
      <CarouselContent className="ml-s0">
        {articles.map((article, index) => (
          <CarouselItem
            className="basis-[calc(100%_-_var(--s5))] pl-s5 md:basis-[calc(50%_-_(var(--s5)/2))] lg:basis-[37.5%]"
            key={index}
          >
            <HgArticleListingStacked
              {...article}
              showSubheading={shouldShowSubheading}
              aspectRatio="16:9"
              size="heading-6"
            />
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="space-between mx-s5 mt-s6 flex flex-row flex-wrap items-center justify-between gap-s2 border-t border-solid border-border-frosted pt-s4">
        <HgUiControls
          onBack={scrollPrev}
          onForward={scrollNext}
          currentIndex={currentIndex}
          numPages={articles.length}
          showCount={false}
          leftIcon="chevron-left"
          rightIcon="chevron-right"
        />
        {buttonLinkProps?.href && (
          <HgButtonLink {...buttonLinkProps} variant="tonal" size="medium" />
        )}
      </div>
    </>
  )
}

export const HgArticleListingOverflow = (props: HgArticleListingOverflowProps) => {
  return (
    <div className="w-full pt-s6">
      <Carousel opts={{align: 'start', loop: true}}>
        <ArticleListingsContainer {...props} />
      </Carousel>
    </div>
  )
}
